import React, { Fragment } from 'react';

import {
  Checkbox,
  Field,
  FormLabel,
  Margin,
  Select,
  SelectItem,
  Spacer,
  Text,
  TextInput,
  Typeahead,
  TypeaheadItem,
} from '@zapier/design-system';

import ImageField from 'app/developer-v3/components/ImageField';
import Link from 'app/common/components/Link';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import { useForm } from 'app/developer-v3/hooks';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';

import PublicIntentWarning from './PublicIntentWarning';
import RoleWarning from './RoleWarning';
import {
  CategoryItems,
  FIELD_VERTICAL_SPACING,
  IntentionItems,
  MAX_DESCRIPTION_CHARS,
  MIN_TITLE_CHARS,
  RoleItems,
  HTTPS_PREFIX,
} from './constants';
import { sanitizeHomepageUrl, replaceRoleLabel } from './utils';

import type { AppFormType as Settings } from 'app/developer-v3/types';

type Props = {
  cancelUrl: string;
  initialSettings: Settings;
  isLoading: boolean;
  isPublished: boolean;
  isSaving: boolean;
  onSubmit: (integrationSettings: Settings) => Promise<void>;
  saveText?: string;
};

function IntegrationSettingsForm({
  cancelUrl,
  initialSettings,
  isLoading,
  isPublished,
  isSaving,
  onSubmit,
  saveText,
}: Props) {
  const form = useForm<Settings>({
    enableReinitialize: true,
    initialValues: {
      appCategory: initialSettings.appCategory,
      description: initialSettings.description,
      homepageUrl: sanitizeHomepageUrl(initialSettings.homepageUrl),

      imageFile: null,
      intention: initialSettings.intention,
      role: initialSettings.role,
      ...(initialSettings.subscription
        ? { subscription: initialSettings.subscription }
        : {}),
      title: initialSettings.title,
    },
    async onSubmit(valuesNext: Settings) {
      const sanitizedHomepageUrl = sanitizeHomepageUrl(valuesNext.homepageUrl);
      const homepageUrlNext = valuesNext.homepageUrl
        ? `${HTTPS_PREFIX}${sanitizedHomepageUrl}`
        : undefined;

      await onSubmit({
        app_category: valuesNext.appCategory,
        description: valuesNext.description,
        homepage_url: homepageUrlNext,
        imageFile: valuesNext.imageFile,
        intention: valuesNext.intention,
        role: valuesNext.role,
        ...(valuesNext.subscription
          ? { subscription: valuesNext.subscription }
          : {}),
        title: valuesNext.title,
      });
      form.setFieldValue('imageFile', null);
      form.setFieldValue('homepageUrl', sanitizedHomepageUrl);
    },
    validate: valuesNext => {
      const { appCategory, description, intention, role, title } = valuesNext;
      const errorsNext: Partial<Record<keyof Settings, string>> = {};

      if (!appCategory) {
        errorsNext.appCategory = 'App category is required.';
      }

      if (description) {
        if (description.length > MAX_DESCRIPTION_CHARS) {
          errorsNext.description = `Description must be less than ${MAX_DESCRIPTION_CHARS} characters.`;
        }
      } else {
        errorsNext.description = 'Description is required.';
      }

      if (!intention) {
        errorsNext.intention = 'Intended audience is required.';
      }

      if (!role) {
        errorsNext.role = 'Role is required.';
      }

      if (title.length < MIN_TITLE_CHARS) {
        errorsNext.title = `Name must be at least ${MIN_TITLE_CHARS} characters.`;
      }

      return errorsNext;
    },
  });

  const isFormReadOnly = isPublished || isLoading || isSaving;

  return (
    <Fragment>
      <Field
        error={form.isTouched.title ? form.errors.title : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Integration Name"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Name this integration. It should match the core branding of your
            application. The name will be displayed in the Zapier{' '}
            <Link href="https://zapier.com/apps" target="_blank">
              app integrations page
            </Link>
            . Example: <code>Trello</code>.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="Integration name"
            name="title"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="Trello"
            value={form.values.title}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.description ? form.errors.description : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Integration Description"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Maximum {MAX_DESCRIPTION_CHARS} characters. Example:{' '}
            <code>
              Trello is a team collaboration tool to organize tasks and keep
              projects on track.
            </code>
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="Integration description"
            isMultiline={true}
            name="description"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="Trello is a team collaboration tool to organize tasks and keep projects on track."
            resize="vertical"
            value={form.values.description}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.homepageUrl ? form.errors.homepageUrl : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        label="App homepage URL"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            The URL of your application's homepage. It will also be used as the
            email domain that would allow team members to join your integration
            as a Collaborator.{' '}
            <Link
              href={`${BASE_DOC_URL_VB}/manage/add-team#self-serve-collaborator-access`}
              target="_blank"
            >
              Learn more
            </Link>
            .
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="App homepage URL"
            name="homepageUrl"
            onChange={form.onChange}
            placeholder="trello.com"
            prependContent={HTTPS_PREFIX}
            value={form.values.homepageUrl}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.imageFile ? form.errors.imageFile : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        label="Logo"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Upload a square, 512x512px or larger transparent PNG image that does
            not include the app name.{' '}
            <Link
              href={`${BASE_DOC_URL_VB}/partners/planning-guide#app-logo`}
              target="_blank"
            >
              Learn more
            </Link>
            .
          </Text>
        )}
        renderInput={() => (
          <ImageField
            imageUrl={
              form.values.imageFile
                ? URL.createObjectURL(form.values.imageFile)
                : initialSettings.image
            }
            isReadOnly={isFormReadOnly}
            onChange={imageFile => {
              form.setFieldValue('imageFile', imageFile);
              form.setFieldTouched('imageFile', true);
            }}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Fragment>
        <Select
          errorMessage={form.isTouched.intention ? form.errors.intention : ''}
          isInvalid={form.isTouched.intention && !!form.errors.intention}
          isDisabled={isFormReadOnly}
          isRequired={true}
          label="Intended Audience"
          defaultSelectedKey={form.values.intention ?? null}
          onBlur={() => form.setFieldTouched('intention', true)}
          onChange={key => form.setFieldValue('intention', key as string)}
          placeholder="Select private or public integration"
        >
          {Object.values(IntentionItems).map(item => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </Select>
        {!isFormReadOnly &&
          form.values.intention === IntentionItems.public.value && (
            <Margin margin="10px 0 0 0">{PublicIntentWarning}</Margin>
          )}
      </Fragment>
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Fragment>
        <Select
          errorMessage={form.isTouched.role ? form.errors.role : ''}
          isInvalid={form.isTouched.role && !!form.errors.role}
          isDisabled={isFormReadOnly}
          isRequired={true}
          label="Role"
          defaultSelectedKey={form.values.role ?? null}
          onBlur={() => form.setFieldTouched('role', true)}
          onChange={key => form.setFieldValue('role', key as string)}
          placeholder="Select your relationship with the app you're integrating with Zapier"
        >
          {Object.values(RoleItems).map(item => (
            <SelectItem key={item.value} value={item.value}>
              {replaceRoleLabel(item.label, form.values.title)}
            </SelectItem>
          ))}
        </Select>
        {!isFormReadOnly && form.values.role === RoleItems.user.value && (
          <Margin margin="10px 0 0 0">{RoleWarning}</Margin>
        )}
      </Fragment>
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Typeahead
        aria-label="Category"
        label="Category"
        helpText="Choose the most appropriate option for your app's core features."
        errorMessage={form.isTouched.appCategory ? form.errors.appCategory : ''}
        isInvalid={!!(form.isTouched.appCategory && form.errors.appCategory)}
        isDisabled={isFormReadOnly}
        isRequired={true}
        selectedKey={form.values.appCategory ?? null}
        onChange={key =>
          form.setFieldValue('appCategory', key ? String(key) : undefined)
        }
        onBlur={() => form.setFieldTouched('appCategory', true)}
        placeholder="Search for the category your app is in"
      >
        {CategoryItems.map(item => (
          <TypeaheadItem key={item.value} id={item.value}>
            {item.label}
          </TypeaheadItem>
        ))}
      </Typeahead>
      {'subscription' in form.values ? (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <Field
            error={form.isTouched.subscription ? form.errors.subscription : ''}
            isDisabled={isFormReadOnly}
            label="Subscribe to email updates about your integration"
            renderInput={() => (
              <FormLabel
                isErrored={
                  form.isTouched.subscription ? form.errors.subscription : ''
                }
              >
                <Checkbox
                  isSelected={form.values.subscription}
                  name="subscription"
                  onChange={(isSelected: boolean) =>
                    form.setFieldValue('subscription', isSelected)
                  }
                >
                  Receive monthly performance reports, tips and Zapier news
                </Checkbox>
              </FormLabel>
            )}
          />
        </Fragment>
      ) : null}
      <SaveButtonGroup
        cancelRoute={cancelUrl}
        cancelText="Cancel"
        isCancelDisabled={isPublished}
        isDisabled={
          isFormReadOnly || !form.isValid || !form.isDirty || form.isSubmitting
        }
        isSaving={isSaving}
        margin="20px 0px 0px 0px"
        onSave={form.onSubmit}
        saveText={saveText}
      />
    </Fragment>
  );
}

export default IntegrationSettingsForm;
